import React, { useState } from 'react';
import { useAuth } from './auth';
import './ClientToTrainerPayments.css';

const DEFAULT_HOURLY_RATE = 60; // example default

const spoofedPayments = [
  {
    id: 1,
    date: '2024-06-01',
    type: 'Session Charge',
    rate: 60,
    durationMinutes: 55,
    subtotal: 55,
    salesTax: 4.13,
    total: 59.13,
    method: 'Charge',
  },
  {
    id: 2,
    date: '2024-06-03',
    type: 'Manual Adjustment',
    amount: 30,
    method: 'Bank Transfer'
  },
  {
    id: 3,
    date: '2024-06-05',
    type: 'Session Charge',
    rate: 60,
    durationMinutes: 65,
    subtotal: 65,
    salesTax: 4.88,
    total: 69.88,
    method: 'Charge',
  },
];

export default function ClientToTrainerPayments() {
  const [hourlyRate, setHourlyRate] = useState(DEFAULT_HOURLY_RATE);

  const [payments, setPayments] = useState(spoofedPayments);

  const { user } = useAuth();

  const [customPayment, setCustomPayment] = useState('');
  const [adjustmentAmount, setAdjustmentAmount] = useState('');
  const [adjustmentLabel, setAdjustmentLabel] = useState('');

  const handleAddAdjustment = () => {
    const amount = parseFloat(adjustmentAmount);
    if (!isNaN(amount) && amount !== 0 && adjustmentLabel.trim()) {
      const newPayment = {
        id: Date.now(),
        date: new Date().toISOString().slice(0, 10),
        type: adjustmentLabel,
        amount: amount,
        method: 'Manual Adjustment'
      };
      setPayments([newPayment, ...payments]);
      setAdjustmentAmount('');
      setAdjustmentLabel('');
    }
  };

  const handleMakePayment = () => {
    const amount = parseFloat(customPayment);
    if (!isNaN(amount) && amount > 0) {
      const newPayment = {
        id: Date.now(),
        date: new Date().toISOString().slice(0, 10),
        type: 'Custom Payment',
        amount: amount,
        method: 'Custom'
      };
      setPayments([newPayment, ...payments]);
      setCustomPayment('');
    }
  };

  let runningBalance = 0;
  const paymentsWithBalance = payments.map(payment => {
    let amt = 0;
    if (payment.type === 'Session Charge') {
      amt = payment.total;
    } else if (typeof payment.amount === 'number') {
      amt = payment.amount;
    }
    runningBalance += amt;
    return { ...payment, resultingBalance: runningBalance };
  });

  const downloadStatement = () => {
    const csv = [
      ['Date', 'Type', 'Rate', 'Duration (min)', 'Subtotal', 'Sales Tax', 'Total', 'Amount', 'Method', 'Resulting Balance'],
      ...paymentsWithBalance.map(p => [
        p.date,
        p.type,
        p.rate || '',
        p.durationMinutes || '',
        p.subtotal || '',
        p.salesTax || '',
        p.total || '',
        p.amount || '',
        p.method,
        p.resultingBalance
      ])
    ].map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'payment_statement.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="clientdashboard-container fogged-card">
      <h2 className="clientdashboard-header">Trainer Hourly Rate</h2>
      <div className="clientdashboard-hourly-rate">
        {user.username === 'trainer' ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input
              type="number"
              min="0"
              value={hourlyRate}
              onChange={e => setHourlyRate(parseFloat(e.target.value) || 0)}
            />
            <span>/ hour</span>
          </div>
        ) : (
          <div style={{ fontSize: 18 }}>
            <span>${hourlyRate}</span> / hour
          </div>
        )}
      </div>
      <div className="clientdashboard-info-box">
        <strong>How charges work:</strong><br />
        When a session is completed, the time of the workout is rounded to the nearest 5 minutes, multiplied by the trainer's hourly rate. A subtotal is calculated, then sales tax is added. The ledger below shows each charge with the rate, duration, subtotal, sales tax, and total. Trainers can make manual adjustments as needed.
      </div>

      {user.username === 'trainer' && (
        <div className="clientdashboard-adjustment-box">
          <h3>Add Manual Adjustment</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8 }}>
            <input
              type="text"
              placeholder="Adjustment Label (e.g. Cash, Venmo, Correction)"
              value={adjustmentLabel}
              onChange={e => setAdjustmentLabel(e.target.value)}
            />
            <input
              type="number"
              placeholder="Amount"
              value={adjustmentAmount}
              onChange={e => setAdjustmentAmount(e.target.value)}
              step="0.01"
            />
            <button onClick={handleAddAdjustment}>Add Adjustment</button>
          </div>
          <div className="info">
            Use for cash, Venmo, or other manual/offline payments or corrections. Use negative amount for refunds or corrections.
          </div>
        </div>
      )}

      {user.username === 'client' && (
        <div className="clientdashboard-custom-payment">
          <label>
            Custom Payment Amount: $
            <input
              type="number"
              value={customPayment}
              onChange={e => setCustomPayment(e.target.value)}
              min="0"
              step="0.01"
            />
          </label>
          <button onClick={handleMakePayment}>Make Payment</button>
        </div>
      )}

      <h2 className="clientdashboard-payment-history-header">Payment History</h2>
      <div style={{ marginBottom: 16, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button className="clientdashboard-download-button" onClick={downloadStatement}>
          Download Statement
        </button>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {paymentsWithBalance.map(payment => (
          <div key={payment.id} className="clientdashboard-payment-card">
            <div><strong>Date:</strong> {payment.date}</div>
            <div><strong>Type:</strong> {payment.type}</div>
            {payment.type === 'Session Charge' && (
              <>
                <div><strong>Rate:</strong> ${payment.rate} / hour</div>
                <div><strong>Duration:</strong> {payment.durationMinutes} min</div>
                <div><strong>Subtotal:</strong> ${payment.subtotal.toFixed(2)}</div>
                <div><strong>Sales Tax:</strong> ${payment.salesTax.toFixed(2)}</div>
                <div><strong>Total:</strong> ${payment.total.toFixed(2)}</div>
              </>
            )}
            {typeof payment.amount === 'number' && payment.type !== 'Session Charge' && (
              <div><strong>Amount:</strong> ${payment.amount}</div>
            )}
            <div><strong>Method:</strong> {payment.method}</div>
            <div><strong>Resulting Balance:</strong> ${payment.resultingBalance.toFixed(2)}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
